import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, Sparkles, UserRoundSearch } from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import { userProfilesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import {
  mergeDiscoverRouteCache,
  readDiscoverRouteCache,
} from "../utils/discoverRouteCache";
import { connectionsAPI } from "../services/api";
import { writeConnectionsRouteCache } from "../utils/connectionsRouteCache";
import "./DiscoverPeople.css";

const getInitial = (name) => (name || "M").trim().charAt(0).toUpperCase();

const formatMemberSince = (date) => {
  if (!date) return "Member";
  return `Since ${new Date(date).toLocaleDateString()}`;
};

const getProfilePath = (person) =>
  person.isCurrentUser ? "/public-profile" : `/users/${person.userId}`;

const DiscoverPeople = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const communitiesSnapshot = location.state?.fromCommunitiesSnapshot || null;
  const initialPeople =
    location.state?.prefetchedPeople ||
    readDiscoverRouteCache()?.discoverPeople ||
    null;
  const [people, setPeople] = useState(initialPeople || []);
  const [loading, setLoading] = useState(!initialPeople);

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: (
          <span className="discover-people-breadcrumb-label">
            <UserRoundSearch size={14} />
            <span>Meet People</span>
          </span>
        ),
        path: null,
      },
    ],
    [],
  );

  const handleBackToCommunities = React.useCallback(() => {
    navigate(communitiesSnapshot?.returnPath || "/communities", {
      state: communitiesSnapshot || undefined,
    });
  }, [communitiesSnapshot, navigate]);

  const loadPeople = React.useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) setLoading(true);
        const response = await userProfilesAPI.getDiscoverPeople();
        const nextPeople = response.data.people || [];
        setPeople(nextPeople);
        mergeDiscoverRouteCache({ discoverPeople: nextPeople });
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load people",
          "error-warning",
        );
        handleBackToCommunities();
      } finally {
        setLoading(false);
      }
    },
    [addToast, handleBackToCommunities],
  );

  useEffect(() => {
    loadPeople(!initialPeople);
  }, [initialPeople, loadPeople]);

  const handleOpenConnections = async () => {
    try {
      const [acceptedRes, incomingRes, sentRes, messagesRes] =
        await Promise.all([
          connectionsAPI.getAcceptedConnections(),
          connectionsAPI.getIncomingRequests(),
          connectionsAPI.getSentRequests(),
          connectionsAPI.getConnections(),
        ]);
      const snapshot = {
        accepted: acceptedRes.data.connections || [],
        incoming: incomingRes.data.requests || [],
        sent: sentRes.data.requests || [],
        messages: messagesRes.data.connections || [],
      };
      writeConnectionsRouteCache(snapshot);
      navigate("/connections", {
        state: { prefetchedConnections: snapshot },
      });
    } catch {
      navigate("/connections");
    }
  };

  if (loading && !communitiesSnapshot) {
    return <Spinner visible={true} />;
  }

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={
        <NavigationButtons
          hideExitButton={true}
          hideActiveButton={true}
          communityRouteState={communitiesSnapshot}
        />
      }
    >
      <div className="discover-people-page">
        <div className="discover-people-intro-row">
          <Button
            className="discover-people-connections-button"
            onClick={handleOpenConnections}
          >
            Connections
          </Button>
          <p className="discover-people-intro">
            Meet learners who have chosen to make their Asasiasa profile public.
          </p>
        </div>

        <section className="discover-people-grid">
          {loading ? (
            <div className="discover-people-empty">
              Loading visible public profiles...
            </div>
          ) : people.length === 0 ? (
            <div className="discover-people-empty">
              No visible public profiles are available right now.
            </div>
          ) : (
            people.map((person) => (
              <article className="discover-people-card" key={person.userId}>
                <div className="discover-people-card-top">
                  <button
                    type="button"
                    className="discover-people-avatar"
                    onClick={() => navigate(getProfilePath(person))}
                    title={
                      person.isCurrentUser ? "Edit profile" : "View profile"
                    }
                    aria-label={
                      person.isCurrentUser ? "Edit profile" : "View profile"
                    }
                  >
                    {getInitial(person.publicDisplayName)}
                  </button>
                  <span className="discover-people-meta-pill">
                    <CalendarDays size={14} />
                    {formatMemberSince(person.memberSince)}
                  </span>
                  {person.isCurrentUser ? (
                    <span className="discover-people-current-pill">You</span>
                  ) : null}
                </div>

                <h2>{person.publicDisplayName || "Asasiasa member"}</h2>
                <p className="discover-people-bio">
                  {person.publicBio || "No public bio yet."}
                </p>

                {person.publicInterests ? (
                  <div className="discover-people-interests">
                    <Sparkles size={14} />
                    <span>{person.publicInterests}</span>
                  </div>
                ) : null}

                <div className="discover-people-actions">
                  <Button
                    className="discover-people-view-button"
                    onClick={() => navigate(getProfilePath(person))}
                  >
                    {person.isCurrentUser ? "Edit Profile" : "View Profile"}
                  </Button>
                </div>
              </article>
            ))
          )}
        </section>
      </div>
    </StudyLayout>
  );
};

export default DiscoverPeople;
